import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import WhatsAppFab from "@/components/WhatsAppFab";

export const Reveal = ({ children, delay = 0, y = 30, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Marquee = ({ items }) => (
  <div className="overflow-hidden border-y border-white/10 py-6 bg-[#111111]" data-testid="editorial-marquee">
    <div className="marquee-track">
      {[...items, ...items].map((t, i) => (
        <span key={i} className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter mx-8 flex items-center gap-8">
          <span className={i % 2 ? "text-outline" : "text-white"}>{t}</span>
          <span className="text-[#E31E24] text-3xl">✦</span>
        </span>
      ))}
    </div>
  </div>
);

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=air-compressors", label: "Compressors" },
  { to: "/shop?category=car-wash-machines", label: "Car Wash" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { cartCount } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src="/indoforce-icon.png"
            alt="Indoforce"
            className="h-9 w-auto"
            width="42"
            height="36"
          />
          <span className="font-display font-black text-xl tracking-tighter uppercase text-white">
            INDOFORCE
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[#E31E24] transition-colors"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="cart-button"
            onClick={() => navigate("/cart")}
            className="relative p-2 hover:text-[#E31E24] transition-colors"
          >
            <ShoppingCart size={22} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#E31E24] text-black text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full">
                {cartCount}
              </span>
            )}
          </button>
          <button className="lg:hidden p-2" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-[0.2em] text-white/80"
            >
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#111111] border-t border-white/10 mt-24" data-testid="site-footer">
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-4 gap-12">
      <div className="md:col-span-2">
        <h3 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">
          Built for <span className="text-[#E31E24]">pressure.</span>
        </h3>
        <p className="text-white/50 mt-6 max-w-md leading-relaxed">
          Air compressors, high-pressure car wash systems, genuine spares & accessories for
          workshops and detailing pros across India.
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-5">Catalog</p>
        <ul className="space-y-3 text-white/70 text-sm">
          <li><Link to="/shop?category=air-compressors" className="hover:text-[#E31E24]">Air Compressors</Link></li>
          <li><Link to="/shop?category=compressor-spares" className="hover:text-[#E31E24]">Compressor Spares</Link></li>
          <li><Link to="/shop?category=car-wash-machines" className="hover:text-[#E31E24]">Car Wash Machines</Link></li>
          <li><Link to="/shop?category=accessories" className="hover:text-[#E31E24]">Accessories</Link></li>
        </ul>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-5">Company</p>
        <ul className="space-y-3 text-white/70 text-sm">
          <li><Link to="/contact" className="hover:text-[#E31E24]">Contact / Enquiry</Link></li>
          <li><a href="/api/feed/google.xml" className="hover:text-[#E31E24]">Google Feed</a></li>
          <li><Link to="/admin" className="hover:text-[#E31E24]">Admin</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 px-6 md:px-10 text-center text-white/40 text-xs uppercase tracking-[0.2em]">
      © {new Date().getFullYear()} Indoforce — Industrial Air & Wash Systems
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="grain min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="relative z-[2]">{children}</main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
